import Axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  OktaAuthResponse,
  OktaFindGroupByNameResponse,
  OktaIntrospectResponse,
  OktaTokenExchangeResponse,
  OktaUserGroupsResponse,
  OktaUserResponse,
} from './types';
import OktaConfig from '../../../config/okta';
import qs from 'qs';

/**
 * Okta Classic Identity Engine API wrapper, to facilitate signing.
 *
 * @see https://developer.okta.com/docs/reference/api/authn/#get-started-with-authentication
 */
class OktaService {
  /**
   * Issuer URL is used for OIDC/OAuth compatible APIs
   */
  private issuerUrl: string | undefined;
  /**
   * API Url is used for Okta specific APIs, i.e. `authn`
   */
  private apiUrl: string | undefined;
  private clientId: string | undefined;
  private clientSecret: string | undefined;
  private adminSecret: string | undefined;
  private adminToken: string | undefined;
  private apiAxios: AxiosInstance;
  private issuerAxios: AxiosInstance;

  private static scope = 'openid profile email groups offline_access';

  constructor() {
    this.issuerUrl = OktaConfig.issuer;
    this.apiUrl = OktaConfig.apiUrl;

    this.clientId = OktaConfig.clientId;
    this.clientSecret = OktaConfig.clientSecret;

    this.adminSecret = OktaConfig.adminSecret;
    this.adminToken = OktaConfig.adminToken;

    const commonAxiosConfig: Partial<AxiosRequestConfig> = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    this.apiAxios = Axios.create({
      baseURL: this.apiUrl,
      ...commonAxiosConfig,
    });

    this.issuerAxios = Axios.create({
      baseURL: this.issuerUrl,
      ...commonAxiosConfig,
    });
  }

  /**
   * @see https://developer.okta.com/docs/reference/api/authn/#forgot-password
   */
  authenticate = async (username: string, password: string) => {
    const body = {
      username,
      password,
      options: {
        multiOptionalFactorEnroll: false,
        warnBeforePasswordExpired: false,
      },
    };
    try {
      const res = await this.apiAxios.post<OktaAuthResponse>('/api/v1/authn', body);
      return res.data;
    } catch (error) {
      if (Axios.isAxiosError(error)) {
        // the error may contain the password in plaintext, we need to avoid logging the entire error here
        if (error.config && error.config.data) {
          try {
            const parsedData = JSON.parse(error.config.data);
            parsedData.password = 'OMITTED';
            error.config.data = JSON.stringify(parsedData);
          } catch (jsonError) {
            // if it's not JSON, nothing to do
          }
        }
      }

      console.error(`OktaService::authenticate error: ${JSON.stringify(error)}`);
      throw error;
    }
  };

  /**
   * @see https://developer.okta.com/docs/reference/api/authn/#forgot-password
   */
  changePassword = async (oldPassword: string, newPassword: string, stateToken: string) => {
    const body = {
      oldPassword,
      newPassword,
      stateToken,
    };

    try {
      const res = await this.apiAxios.post<OktaAuthResponse>('/api/v1/authn/credentials/change_password', body);
      return res.data;
    } catch (error) {
      console.error(`OktaService::changePassword error: ${JSON.stringify(error)}`);
      throw error;
    }
  };

  /**
   * @see https://developer.okta.com/docs/reference/api/authn/#forgot-password
   */
  forgotPassword = async (username: string) => {
    const factorType = 'EMAIL';
    const body = {
      username,
    };

    const res = await this.apiAxios.post<OktaAuthResponse>('/api/v1/authn/recovery/password', body);
    return res.data;
  };

  createSession = async (sessionToken: string) => {
    const body = {
      sessionToken,
    };

    const res = await this.apiAxios.post<OktaAuthResponse>('/api/v1/sessions', body);
    return res.data;
  };

  /**
   * @see https://developer.okta.com/docs/guides/refresh-tokens/main/#use-a-refresh-token
   * @see {exchangeCodeForTokens}
   */
  refreshSession = async (refreshToken: string) => {
    try {
      const baseUrlPath = process.env.NEXT_PUBLIC_OKTA_LOGIN_URL;

      // for some reason, we need to send in a x-www-form-encoded-body and get back JSON...
      const res = await this.issuerAxios.post<OktaTokenExchangeResponse>(
        `/v1/token`,
        qs.stringify({
          client_id: this.clientId,
          client_secret: this.clientSecret,
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          scope: `${OktaService.scope}`,
          redirect_uri: `${baseUrlPath}/login_successful`,
        }),
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );
      return res.data;
    } catch (error) {
      console.error(`OktaService::refreshSession error: ${JSON.stringify(error)}`, { error });
      throw error;
    }
  };

  validateSession = async (sessionToken: string) => {};

  closeCurrentSession = async (sessionToken: string) => {};

  getCurrentSession = async (sessionToken: string) => {};

  /**
   * Returns a 204 No Content response regardless if a success or failure, due to security reasons
   *
   * TODO this is not ideal since it logs the user out of any Okta applications. Is there a better way?
   * We don't have a sessionId to do an API call, can we redirect the user to Okta to ask them to logout?
   *
   * @see https://developer.okta.com/docs/reference/api/users/#clear-user-sessions
   * @param oktaUserId
   * @returns
   */
  clearUserSession = async (oktaUserId: string) => {
    // in case we have old sessions w/o a user id
    if (!oktaUserId) {
      return;
    }
    try {
      const res = await this.apiAxios.delete<undefined>(`/api/v1/users/${oktaUserId}/sessions?oauthTokens=true`, {
        headers: {
          Authorization: `SSWS ${this.adminToken}`,
        },
      });
      return res.data;
    } catch (error) {
      console.error(`OktaService::clearUserSession error: ${JSON.stringify(error)}`);
      throw error;
    }
  };

  /**
   *
   * @param sessionToken temporary session token
   * @param redirectUrl where Okta should redirect to
   * @param nonce unique value used for security purposes, to prevent reply attacks
   * @returns Absolute URL we should redirect to
   */
  getSessionRedirectUrl = (sessionToken: string, redirectUrl: string, state: string, nonce: string) => {
    return (
      `${this.issuerUrl}/v1/authorize?` +
      `client_id=${this.clientId}&` +
      `response_type=code&` +
      `scope=${OktaService.scope}&` +
      `prompt=none&` +
      `redirect_uri=${redirectUrl}&` +
      `state=${state}&` +
      `nonce=${nonce}&` +
      `sessionToken=${sessionToken}`
    );
    // return `https://${this.apiUrl}login/sessionCookieRedirect?token=${sessionToken}&redirectUrl=${redirectUrl}`;
  };

  /**
   * @see https://developer.okta.com/docs/reference/api/users/#get-user
   */
  getUserInfo = async (oktaUserId: string) => {
    try {
      const res = await this.apiAxios.get<OktaUserResponse>(`/api/v1/users/${oktaUserId}`, {
        headers: {
          Authorization: `SSWS ${this.adminToken}`,
        },
      });
      return res.data;
    } catch (error) {
      console.error(`OktaService::getUserInfo error: ${JSON.stringify(error)}`);
      throw error;
    }
  };

  updateUserSelectedSchema = async (oktaUserId: string, selectedSchema: string) => {
    try {
      // TODO Q2022-3524 - Fix response type
      const res = await this.apiAxios.post<OktaUserResponse>(
        `/api/v1/users/${oktaUserId}`,
        {
          profile: {
            default_schema: selectedSchema,
          },
        },
        {
          headers: {
            Authorization: `SSWS ${this.adminToken}`,
          },
        }
      );
      return res.data;
    } catch (error) {
      console.error(`OktaService::updateUserSelectedSchema error: ${JSON.stringify(error)}`);
      throw error;
    }
  };

  getUserGroups = async (oktaUserId: string) => {
    try {
      const res = await this.apiAxios.get<OktaUserGroupsResponse>(`/api/v1/users/${oktaUserId}/groups`, {
        headers: {
          Authorization: `SSWS ${this.adminToken}`,
        },
      });
      return res.data;
    } catch (error) {
      console.error(`OktaService::getUserGroups error: ${JSON.stringify(error)}`);

      // try to log to help other developers check known issues...
      if (Axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          console.error('OktaService::getUserGroups: response was 401, is the API Token active and valid?');
        }
      }

      throw error;
    }
  };

  findGroupIdByGroupName = async (groupName: string) => {
    try {
      const res = await this.apiAxios.get<OktaFindGroupByNameResponse>(`/api/v1/groups/`, {
        headers: {
          Authorization: `SSWS ${this.adminToken}`,
        },
        params: {
          q: `${groupName}`,
        },
      });
      return res.data;
    } catch (error) {
      console.error(`OktaService::getUserGroups error: ${JSON.stringify(error)}`);

      // try to log to help other developers check known issues...
      if (Axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          console.error('OktaService::findGroupIdByGroupName: response was 401, is the API Token active and valid?');
        }
      }

      throw error;
    }
  };

  /**
   * @see https://developer.okta.com/docs/reference/api/groups/#add-user-to-group
   */
  addUserToGroup = async (oktaUserId: string, groupId: string) => {
    try {
      const res = await this.apiAxios.put<undefined>(`/api/v1/groups/${groupId}/users/${oktaUserId}`, undefined, {
        headers: {
          Authorization: `SSWS ${this.adminToken}`,
        },
      });
      return res.data;
    } catch (error) {
      console.error(`OktaService::addUserToGroup error: ${JSON.stringify(error)}`);

      // try to log to help other developers check known issues...
      if (Axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          console.error('OktaService::addUserToGroup: response was 401, is the API Token active and valid?');
        }
      }

      throw error;
    }
  };

  /**
   * @see https://developer.okta.com/docs/reference/api/groups/#remove-user-from-group
   */
  removeUserFromGroup = async (oktaUserId: string, groupId: string) => {
    try {
      const res = await this.apiAxios.delete<undefined>(`/api/v1/groups/${groupId}/users/${oktaUserId}`, {
        headers: {
          Authorization: `SSWS ${this.adminToken}`,
        },
      });
      return res.data;
    } catch (error) {
      console.error(`OktaService::removeUserFromGroup error: ${JSON.stringify(error)}`);

      // try to log to help other developers check known issues...
      if (Axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          console.error('OktaService::removeUserFromGroup: response was 401, is the API Token active and valid?');
        }
      }

      throw error;
    }
  };

  /**
   *
   * @see https://developer.okta.com/docs/reference/api/oidc/#token
   */
  exchangeCodeForTokens = async (code: string) => {
    try {
      const baseUrlPath = process.env.NEXT_PUBLIC_OKTA_LOGIN_URL;

      // for some reason, we need to send in a x-www-form-encoded-body and get back JSON...
      const res = await this.issuerAxios.post<OktaTokenExchangeResponse>(
        `/v1/token`,
        qs.stringify({
          client_id: this.clientId,
          client_secret: this.clientSecret,
          grant_type: 'authorization_code',
          code,
          redirect_uri: `${baseUrlPath}/login_successful`,
        }),
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );
      return res.data;
    } catch (error) {
      console.error(`OktaService::exchangeCodeForTokens error: ${JSON.stringify(error)}`, error);
      throw error;
    }
  };

  introspectIdToken = async (idToken: string) => {
    try {
      const introspectData = {
        token: idToken,
        token_type_hint: 'id_token',
      };

      const res = await this.issuerAxios.post<OktaIntrospectResponse>(
        `/api/v1/introspect?client_id=${this.clientId}`
        // {
        // auth: { username: this.clientId as string, password: this.adminSecret as string },
        // }
      );
      return res.data;
    } catch (error) {
      console.error(`OktaService::introspectToken error: ${JSON.stringify(error)}`);
      throw error;
    }
  };
}

export default new OktaService();
